import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import FormField from './FormField';
import { showToast } from './Toast';

// Mirrors the backend Services::Phone::E164::US_E164_REGEX so the field is
// validated inline before we ever hit the network (GSE-12082 shared E.164 rule).
const US_E164_REGEX = /^\+1[2-9]\d{9}$/;

// Version of the rendered consent copy captured with each opt-in. Bump when the
// checkbox/footnote wording materially changes so audit history stays honest.
const SMS_CONSENT_TEXT_VERSION = '2026-09-ppp-landing-v1';

const DEFAULT_CTA_TEXT = 'Sign Up';
const DEFAULT_CHECKBOX_TEXT =
  'By entering the phone number and checking the box, I agree to receive ' +
  'marketing SMS messages, information, and offers.';
const DEFAULT_LEGAL_FOOTNOTE =
  'Consent is not a condition of purchase. Message and data rates may apply. ' +
  'Max 4 msgs/mo. Reply HELP for help or STOP to cancel. For additional ' +
  'information, view our ' +
  '<a href="/terms" target="_blank" rel="noreferrer" class="underline hover:no-underline">terms/conditions</a>' +
  ' and ' +
  '<a href="/privacy" target="_blank" rel="noreferrer" class="underline hover:no-underline">privacy policy</a>.';

const RECORD_SMS_OPT_IN_MUTATION = `
  mutation RecordSmsOptIn($input: SmsOptInInput!) {
    recordSmsOptIn(input: $input) {
      success
      message
    }
  }
`;

// RudderStack anonymousId stitch key — mirrors SignUpService/SignInService.
const getAnonymousId = () => {
  if (window.gon?.anonymous_id) return window.gon.anonymous_id;
  const match = document.cookie.match(/(?:^|; )anonymous_id=([^;]*)/);
  if (match) return decodeURIComponent(match[1]);
  if (window.rudderanalytics?.getAnonymousId) return window.rudderanalytics.getAnonymousId();
  return undefined;
};

const SectionSmsOptIn = ({
  ctaButtonText,
  smsCheckboxText,
  smsLegalFootnote,
  containerClasses,
  backgroundColor = '#FFFFFF',
}) => {
  // Identity carried into the opt-in. external_id is the logged-in user's own id
  // when signed in, else the email-link URL param (anonymous recipients). PPP
  // pages expose the signed-in user via window.user_data (see the shared _ga
  // partial), not gon.user — the same source _analytics.html.erb uses for
  // external_id; gon.user is a fallback for any surface that sets it.
  const { seededEmail, urlExternalId, loggedInId } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const userId = window.user_data?.user_id || window.gon?.user?.id;
    return {
      seededEmail: window.user_data?.user_email || window.gon?.user?.email || undefined,
      urlExternalId: params.get('external_id') || undefined,
      loggedInId: userId ? String(userId) : undefined,
    };
  }, []);

  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [phoneError, setPhoneError] = useState(null);
  const [consentError, setConsentError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const classes = `px-8 py-14 md:px-24 lg:py-16 w-full ${containerClasses || ''}`;

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (isSubmitting) return;

      let valid = true;
      if (!US_E164_REGEX.test(phone)) {
        setPhoneError('Please enter a valid US phone number.');
        valid = false;
      } else {
        setPhoneError(null);
      }
      if (!consent) {
        setConsentError('Please agree to receive SMS messages to sign up.');
        valid = false;
      } else {
        setConsentError(null);
      }
      if (!valid) return;

      // Signed-in user's own id wins over a URL-supplied one (which may be a
      // stale or foreign id from an email link); the URL value is the anonymous
      // fallback.
      const externalId = loggedInId || urlExternalId;
      const input = {
        phone,
        smsSource: 'LANDING_PAGE',
        consentTextVersion: SMS_CONSENT_TEXT_VERSION,
      };
      if (seededEmail) input.email = seededEmail;
      const anonymousId = getAnonymousId();
      if (anonymousId) input.anonymousId = anonymousId;
      if (externalId) input.externalId = externalId;

      setIsSubmitting(true);
      try {
        const res = await fetch('/graphql', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            // Route the opt-in to THIS brand's subscription group. Server reads
            // x-brand-id (context[:current_brand]); never a different brand.
            'x-brand-id': window.gon?.current_brand || '',
          },
          body: JSON.stringify({
            query: RECORD_SMS_OPT_IN_MUTATION,
            variables: { input },
          }),
        });
        const json = await res.json();
        const result = json?.data?.recordSmsOptIn;

        if (json?.errors?.length || !result?.success) {
          const message =
            result?.message || json?.errors?.[0]?.message ||
            'Something went wrong. Please try again.';
          showToast(message, 'error');
          return;
        }

        // Client-side GA4 lead, mirroring the SMS modal submits. The
        // standardized RudderStack -> Braze generate_lead (channel: sms) is
        // fired server-side by the recordSmsOptIn mutation.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'generate_lead',
          method: 'Landing Page SMS',
          ...(seededEmail ? { user_email: seededEmail } : {}),
          ...(externalId ? { external_id: externalId } : {}),
        });

        setIsSuccess(true);
      } catch (err) {
        showToast('Something went wrong. Please try again.', 'error');
      } finally {
        setIsSubmitting(false);
      }
    },
    [consent, isSubmitting, loggedInId, phone, seededEmail, urlExternalId],
  );

  return (
    <div className={classes} style={{ backgroundColor, fontFamily: 'Poppins, sans-serif' }}>
      <div className="max-w-[600px] mx-auto">
        {isSuccess ? (
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1C2938]">
              You&rsquo;re <span className="text-[#49C44F]">Almost There!</span>
            </h2>
            <p className="text-base text-[#1C2938] mt-4">
              Reply YES to our text to make it official. Travel tips &amp; exclusive
              offers await.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <FormField
              type="phone"
              label=""
              placeholder="555 - 555 - 5555"
              value={phone}
              onChange={setPhone}
              error={phoneError}
            />

            <label className="flex items-start gap-3 mt-4 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-[#1C2938]"
              />
              <span className="text-[14px] leading-[20px] text-[#252525]">
                {parse(smsCheckboxText || DEFAULT_CHECKBOX_TEXT)}
              </span>
            </label>
            {consentError && (
              <p className="text-red-600 text-[12px] mt-1">{consentError}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-[56px] mt-6 rounded-lg bg-[#1C2938] text-white
                text-[16px] font-semibold hover:opacity-90 transition-opacity
                disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {isSubmitting ? 'Signing Up...' : ctaButtonText || DEFAULT_CTA_TEXT}
            </button>

            <div className="text-[12px] leading-[16px] text-[#6E6E6E] mt-6 [&_a]:text-[#6E6E6E]">
              {parse(smsLegalFootnote || DEFAULT_LEGAL_FOOTNOTE)}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

SectionSmsOptIn.propTypes = {
  ctaButtonText: PropTypes.string,
  smsCheckboxText: PropTypes.string,
  smsLegalFootnote: PropTypes.string,
  containerClasses: PropTypes.string,
  backgroundColor: PropTypes.string,
};

export default SectionSmsOptIn;
